'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { ButtonWithHoverReveal } from './ButtonWithHoverReveal'
import { useLanguage } from '../../providers/LanguageProvider'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay,
    },
  }),
}

const ContactSection = () => {
  const pathname = usePathname()
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  //  State for messages
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusType, setStatusType] = useState<'success' | 'error' | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  //  Auto-hide after 5 seconds
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage(null)
        setStatusType(null)
      }, 5000) // 5 seconds

      return () => clearTimeout(timer)
    }
  }, [statusMessage])

  const handleClick = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    const form = e.currentTarget
    const formData = new FormData(form)
    formData.append('origin', `${process.env.NEXT_PUBLIC_SERVER_URL}${pathname}`)

    try {
      const res = await fetch(`/api/submit-lets-connect`, {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) throw new Error('Failed to send enquiry')

      setStatusType('success')
      setStatusMessage(isArabic ? 'تم إرسال النموذج بنجاح!' : 'Form submitted successfully!')
      form.reset()
    } catch (error) {
      console.error(error)
      setStatusType('error')
      setStatusMessage(isArabic ? 'فشل إرسال النموذج' : 'Failed to submit form')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="w-full py-10 lg:pt-28 lg:pb-[210px] 2xl:py-36 h-auto bg-white relative flex justify-center -mt-[1px]">
      <div
        className={clsx(
          'section-container flex flex-col md:flex-row lg:justify-between gap-6 lg:gap-8 xl:gap-10 2xl:gap-16 h-auto',
          isArabic && 'text-right',
        )}
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        {/* Left Side - Form */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          className="w-full lg:w-[62%] flex flex-col justify-between space-y-8 h-full"
        >
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              viewport={{ once: true }}
              className={clsx(
                'text-[24px] sm:text-[26px] md:text-[30px] xl:text-[42px] 2xl:text-[42px] font-semibold text-[#414141] leading-tight',
                isArabic ? 'mb-10 sm:mb-12 md:mb-12 xl:mb-20' : 'mb-10 sm:mb-12 md:mb-16',
              )}
            >
              {isArabic ? 'هل لديك سؤال؟ دعنا نتواصل.' : "Have A Question? Let's Connect."}
            </motion.h2>

            <form onSubmit={handleClick} className="md:space-y-10 space-y-4 ">
              <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                <input
                  type="text"
                  name="botCheck"
                  autoComplete="new-password"
                  className="hidden"
                  tabIndex={-1}
                />
                <div className="w-full">
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {isArabic ? 'الاسم الكامل' : 'Full Name'}
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="name"
                    placeholder={isArabic ? 'الاسم الكامل' : 'Full Name'}
                    className="w-full px-3 py-4 border-b border-gray-500 bg-[#f5f5f5] placeholder-gray-400 text-[15px] md:text-[14px] text-gray-700 focus:outline-none"
                    required
                    minLength={2}
                    maxLength={100}
                    pattern="^[A-Za-z\s]{2,100}$"
                    title="Only alphabets are allowed"
                  />
                </div>
                <div className="w-full">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {isArabic ? 'البريد الإلكتروني' : 'E-Mail'}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder={isArabic ? 'عنوان البريد الإلكتروني' : 'E-Mail Address'}
                    className="w-full px-3 py-4 border-b border-gray-500 bg-[#f5f5f5] placeholder-gray-400 text-[15px] md:text-[14px] text-gray-700 focus:outline-none"
                    required
                    maxLength={120}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 sm:gap-5 md:gap-6">
                <div className="w-full">
                  <label
                    htmlFor="contactNumber"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {isArabic ? 'رقم الاتصال' : 'Contact Number'}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder={isArabic ? 'رقم الاتصال' : 'Contact Number'}
                    className={`w-full px-3 py-4 border-b border-gray-500 bg-[#f5f5f5] placeholder-gray-400 text-[15px] md:text-[14px] text-gray-700 focus:outline-none ${
                      isArabic
                        ? 'text-right placeholder:text-right'
                        : 'text-left placeholder:text-left'
                    }`}
                    required
                    maxLength={15}
                    pattern="^[0-9]{7,15}$"
                    title="Enter a valid phone number (digits only, 7–15 digits)"
                    onInput={(e:any) => {
                      // Remove non-digit characters as user types
                      const input = e.currentTarget
                      input.value = input.value.replace(/\D/g, '')
                    }}
                  />
                </div>

                <div className="w-full">
                  <label
                    htmlFor="industry"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {isArabic ? 'اختر الصناعة' : 'Select Industry'}
                  </label>
                  <input
                    type="text"
                    id="industry"
                    name="industry"
                    placeholder={isArabic ? 'الصناعة' : 'Industry'}
                    className="w-full px-3 py-4 border-b border-gray-500 bg-[#f5f5f5] placeholder-gray-400 text-[15px] md:text-[14px] text-gray-700 focus:outline-none"
                    required
                    minLength={2}
                    maxLength={80}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {isArabic ? 'أرسل رسالة' : 'Send a Message'}
                </label>
                <textarea
                  id="message"
                  name="message"
                  placeholder={isArabic ? 'رسالتك' : 'Message'}
                  rows={5}
                  className="w-full h-[180px] sm:h-[200px] md:h-[135px] px-3 py-3.5 border-b-[.5px] border-gray-500 bg-[#f5f5f5] placeholder-gray-400 text-[15px] md:text-[14px] text-gray-700 resize-none focus:outline-none"
                  maxLength={1000}
                />
              </div>

              <div
                className={clsx(
                  'mt-6 sm:mt-8 md:mt-14',
                  isArabic ? '-mr-[12px] md:mr-0' : '-ml-[12px] md:ml-0',
                )}
              >
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ButtonWithHoverReveal
                    disabled={isSubmitting}
                    text={
                      isSubmitting
                        ? isArabic
                          ? 'يتم الإرسال...'
                          : 'Submitting...'
                        : isArabic
                          ? 'إرسال استفسار'
                          : 'Submit Enquiry'
                    }
                  />
                </button>
              </div>

              {/* ✅ Inline status message */}
              {statusMessage && (
                <p
                  className={clsx(
                    'mt-4 text-sm font-medium transition-opacity duration-500',
                    statusType === 'success' ? 'text-green-600' : 'text-red-600',
                  )}
                >
                  {statusMessage}
                </p>
              )}
            </form>
          </div>
        </motion.div>

        {/* Right Side - Image & Text */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          custom={0.4}
          viewport={{ once: true }}
          className="w-full h-full flex flex-col md:block lg:w-[35%]"
        >
          <motion.h3
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="text-lg order-1 sm:text-xl xl:text-[22px] font-semibold text-[#414141] md:mb-2"
          >
            {isArabic
              ? 'احصل على الحل المناسب - بسرعة وأمان وبتخصيص كامل.'
              : 'Get The Right Solution-Fast, Secure, And Tailored To You.'}
          </motion.h3>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="text-[15px] sm:text-base mt-4 md:mt-0 order-3 text-[#666666] leading-relaxed"
          >
            {isArabic
              ? 'أخبرنا بما تحتاجه، وسيرد فريقنا بحل أو عرض سعر مخصص لك.'
              : 'Tell us what you need, and our team will respond with a customized solution or quote.'}
          </motion.p>
          <div className="relative w-full h-[350px] order-2 md:h-[400px] lg:h-[360px] xl:h-[490px] md:mt-7 mt-4">
            <Image
              src="https://d3lvumh1qhmw7p.cloudfront.net/contact-calling-man.jpeg" ///images/home/contact-calling-man.jpeg
              alt={
                isArabic
                  ? 'فريقنا جاهز لمساعدتك'
                  : 'A person in traditional Arab dress holds a vintage telephone to their ear, standing against a light blue background. '
              }
              fill
              className="w-full object-cover object-top rounded-sm"
            />
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ContactSection
